import type { PriceSimulationProductCombinationOptionDto } from '@eneco/dc-be/client/types.gen';

const getProductNames = (option: PriceSimulationProductCombinationOptionDto): Set<string> =>
  new Set(
    [option.electricityProduct?.productName, option.gasProduct?.productName].filter((name): name is string =>
      Boolean(name),
    ),
  );

export const findMatchingProductCombination = (
  productCombinationOptions: PriceSimulationProductCombinationOptionDto[],
  orderedProductNames: string[],
): PriceSimulationProductCombinationOptionDto | undefined => {
  const orderedNames = new Set(orderedProductNames.filter(Boolean));
  if (orderedNames.size === 0) {
    return undefined;
  }

  const fullCombination = productCombinationOptions.find(option => {
    const combinationNames = getProductNames(option);
    return combinationNames.size === orderedNames.size && [...combinationNames].every(name => orderedNames.has(name));
  });
  if (fullCombination) {
    return fullCombination;
  }

  const isSelectedProductOption = (option: PriceSimulationProductCombinationOptionDto): boolean => {
    const optionNames = getProductNames(option);
    return optionNames.size > 0 && [...optionNames].every(name => orderedNames.has(name));
  };
  const electricityOption = productCombinationOptions.find(
    option => isSelectedProductOption(option) && orderedNames.has(option.electricityProduct?.productName ?? ''),
  );
  const gasOption = productCombinationOptions.find(
    option => isSelectedProductOption(option) && orderedNames.has(option.gasProduct?.productName ?? ''),
  );

  const matchedNames = new Set(
    [electricityOption?.electricityProduct?.productName, gasOption?.gasProduct?.productName].filter(
      (name): name is string => Boolean(name),
    ),
  );
  if (matchedNames.size !== orderedNames.size) {
    return undefined;
  }

  const contributingOptions = [
    ...new Set(
      [electricityOption, gasOption].filter(
        (option): option is PriceSimulationProductCombinationOptionDto => option !== undefined,
      ),
    ),
  ];
  const hasNonConditionalPromoTotal = contributingOptions.some(
    option => option.totalYearlyPriceWithNonCondtitionalPromo != null,
  );

  return {
    ...contributingOptions[0],
    electricityProduct: electricityOption?.electricityProduct,
    gasProduct: gasOption?.gasProduct,
    totalYearlyPrice: contributingOptions.reduce((total, option) => total + option.totalYearlyPrice, 0),
    totalYearlyPriceWithNonCondtitionalPromo: hasNonConditionalPromoTotal
      ? contributingOptions.reduce(
          (total, option) => total + (option.totalYearlyPriceWithNonCondtitionalPromo ?? option.totalYearlyPrice),
          0,
        )
      : null,
    totalConditionalPromo: contributingOptions.reduce((total, option) => total + option.totalConditionalPromo, 0),
    promoTexts: [...new Set(contributingOptions.flatMap(option => option.promoTexts))],
    promoDisclaimers: contributingOptions.flatMap(option => option.promoDisclaimers),
    isWelcomePromo: contributingOptions.some(option => option.isWelcomePromo),
    sortOrder: Math.min(...contributingOptions.map(option => option.sortOrder)),
  };
};
